import { Box, MenuItem, TableCell, TableRow, Typography } from '@mui/material';
import DialogSimple from '@/components/dialog/DialogSimple';
import { Iconify, Label } from '@/components';
import { TableMoreMenu } from '@/components/table';
import { use3DotMenu } from '@/hooks/use3DotMenu';
import useToggle from '@/hooks/useToggle';
import { fDateTime, fToNow } from '@/utils/formatTime';
import type { AuthSession } from '@/features/users/shared/types';

type UserSessionsTableRowProps = {
  row: AuthSession;
  isDeleting: boolean;
  capabilityAwareActions?: boolean;
  canRevokeAllSessions?: boolean;
  onDeleteRow: (sessionId: string) => void;
};

export default function UserSessionsTableRow({
  row,
  isDeleting,
  capabilityAwareActions = false,
  canRevokeAllSessions = false,
  onDeleteRow,
}: UserSessionsTableRowProps) {
  const { openMenu, handleCloseMenu, handleOpenMenu } = use3DotMenu();
  const {
    toggle: isDeleteDialogOpen,
    onOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
  } = useToggle();

  const deviceLabel =
    row.label || row.deviceName || row.deviceType || 'Unknown device';
  const environmentLabel = [row.browser, row.os, row.deviceType]
    .filter(Boolean)
    .join(' • ');
  const loginSourceLabel = row.loginSource || 'N/A';
  const ipAddressLabel = row.ipAddress || 'N/A';
  const sessionReference = row.sessionId.slice(0, 8);
  const revokeAllFallbackRecommended =
    capabilityAwareActions &&
    (row.capabilities?.individualForceLogoutSupported === false ||
      row.capabilities?.revokeAllFallbackRecommended === true);

  const handleDelete = () => {
    closeDeleteDialog();
    onDeleteRow(row.sessionId);
  };

  return (
    <>
      <DialogSimple
        dialogTitle={`Remove session: ${deviceLabel}`}
        open={isDeleteDialogOpen}
        onClickButtonClose={closeDeleteDialog}
        onClickButtonLoading={handleDelete}
        loadingButton
        loadingButtonText="Remove session"
        loadingButtonColor="error"
        disagreeButton
        disagreeButtonText="Cancel"
        disagreeButtonColor="inherit"
        isSubmitting={isDeleting}
      >
        <Box sx={{ px: 3 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {row.current
              ? 'Are you sure you want to remove the current session? This will sign out this device.'
              : 'Are you sure you want to remove this session?'}
          </Typography>
          {revokeAllFallbackRecommended && canRevokeAllSessions ? (
            <Typography variant="body2" color="text.secondary">
              Use the toolbar action when you need guaranteed logout across all
              active sessions.
            </Typography>
          ) : null}
        </Box>
      </DialogSimple>

      <TableRow hover sx={{ verticalAlign: 'top' }}>
        <TableCell width={10} />

        <TableCell align="left">
          <Typography variant="subtitle2" noWrap>
            {deviceLabel}
          </Typography>
          {environmentLabel ? (
            <Typography variant="body2" color="text.secondary" noWrap>
              {environmentLabel}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" noWrap>
            Session {sessionReference}
          </Typography>
        </TableCell>

        <TableCell align="left">{ipAddressLabel}</TableCell>

        <TableCell align="left">
          <Typography variant="body2" noWrap>
            {loginSourceLabel}
          </Typography>
        </TableCell>

        <TableCell align="left">
          <Typography variant="body2">{fDateTime(row.createdAt)}</Typography>
        </TableCell>

        <TableCell align="left">
          <Typography variant="body2">{fDateTime(row.lastSeenAt)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {fToNow(row.lastSeenAt)}
          </Typography>
        </TableCell>

        <TableCell align="left">
          <Typography variant="body2">{fDateTime(row.expiresAt)}</Typography>
        </TableCell>

        <TableCell align="left">
          <Label color={row.current ? 'success' : 'default'}>
            {row.current ? 'Current' : 'Active'}
          </Label>
          {revokeAllFallbackRecommended ? (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ display: 'block', mt: 0.75, lineHeight: 1.3 }}
            >
              {canRevokeAllSessions
                ? 'Use toolbar action for guaranteed logout'
                : 'Superadmin required for guaranteed logout'}
            </Typography>
          ) : null}
        </TableCell>

        <TableCell align="right">
          <TableMoreMenu
            open={openMenu}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            actions={
              <MenuItem
                onClick={() => {
                  openDeleteDialog();
                  handleCloseMenu();
                }}
                sx={{ color: 'error.main' }}
              >
                <Iconify icon="eva:trash-2-outline" />
                Remove session
              </MenuItem>
            }
          />
        </TableCell>
      </TableRow>
    </>
  );
}
